import { useState } from 'react';
import { AlertTriangle, X, ShieldCheck, Loader2 } from 'lucide-react';
import { pipelinesApi } from '../../api/client';

export default function EnvCheckModal({ missingKeys, repo, triggerName, onConfirm, onCancel }) {
  const [values, setValues] = useState(() =>
    Object.fromEntries(missingKeys.map(k => [k, '']))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSaveAndBuild() {
    setSaving(true);
    setError(null);
    try {
      // Blank fields are left out of Secret Manager entirely — only variables
      // the user actually gave a value for get written there. `values` itself
      // (including the blanks) still goes to onConfirm so the build proceeds
      // with whatever was actually entered.
      const filledSecrets = Object.fromEntries(
        Object.entries(values).filter(([, v]) => v?.trim())
      );
      if (Object.keys(filledSecrets).length > 0) {
        await pipelinesApi.createSecrets({ secrets: filledSecrets, ...(triggerName ? { triggerName } : {}) });
      }
      // Tell the caller exactly which keys actually got saved (savedKeys) vs.
      // just typed (values) — a key left blank is still missing from Secret
      // Manager, so callers shouldn't treat it as resolved.
      onConfirm(values, Object.keys(filledSecrets));
    } catch (err) {
      setError(err.error || err.message || 'Failed to save secrets');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md mx-4 card border border-orange-500/30 shadow-2xl animate-fade-in">

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0">
              <AlertTriangle size={16} className="text-orange-500" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">New environment variables detected</p>
              <p className="text-xs text-slate-500 mt-0.5">{repo}</p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="w-6 h-6 rounded-md hover:bg-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-900 transition-colors shrink-0"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 max-h-72 overflow-y-auto">
          <p className="text-xs text-slate-400">
            This branch introduces <span className="text-blue-700 font-medium">{missingKeys.length}</span> variable{missingKeys.length !== 1 ? 's' : ''} not yet in Secret Manager.
            Provide their values to proceed with the build.
          </p>

          <div className="space-y-3">
            {missingKeys.map(key => (
              <div key={key}>
                <label className="block text-[10px] font-medium text-blue-700 mb-1">{key}</label>
                <input
                  type="password"
                  className="input text-xs font-mono border-blue-300 focus:border-blue-500 focus:ring-blue-500/20"
                  placeholder="Enter secret value…"
                  value={values[key]}
                  onChange={e => setValues(v => ({ ...v, [key]: e.target.value }))}
                  autoComplete="off"
                />
              </div>
            ))}
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              <AlertTriangle size={12} className="text-red-400 shrink-0 mt-0.5" />
              <span className="text-xs text-red-400">{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200">
          <button onClick={onCancel} className="btn-ghost py-1.5 px-4 text-xs">
            Cancel
          </button>
          <button
            onClick={handleSaveAndBuild}
            disabled={saving}
            className="flex items-center gap-2 py-1.5 px-4 rounded-lg text-xs font-medium bg-accent-purple hover:bg-accent-purple-light disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
          >
            {saving
              ? <><Loader2 size={12} className="animate-spin" /> Saving…</>
              : <><ShieldCheck size={12} /> Save & Build</>
            }
          </button>
        </div>

      </div>
    </div>
  );
}
